import React, { forwardRef, memo, useCallback, useMemo, useState } from "react";
import WEBHOOK_LOGO_IMG from "../../img/webhook-logo.png";
import SettingsModule, { SettingsModuleSection } from "./SettingsModule";

import {
	Asset,
	Card,
	CodeSnippet,
	FONT_COLOR,
	FONT_SIZE,
	PaddedContainer,
	SPACING,
	TabSwitcher,
	Text,
	TitleRow,
} from "@disco/disco_core";
import {
	INTEGRATIONS_MODULE_PRIVACY_WEBHOOK_URL,
	INTEGRATIONS_MODULE_WEBHOOK_CODE,
	INTEGRATIONS_MODULE_WEBHOOK_CODE_LANGUAGES,
} from "../../utils/getIntegrationsPrivacyWebhookCode";
import "./privacy-module.css";

const INTEGRATION_MODULE_WEBHOOK_INSTRUCTIONS = (user) => {
	const {
		publisher: { remote_id: publisherId },
	} = user;
	return [
		{
			heading: "URL",
			description: `${INTEGRATIONS_MODULE_PRIVACY_WEBHOOK_URL}`,
		},
		{ heading: "Content-Type", description: "application/json" },
		{ heading: "publisher-id", description: publisherId },
	];
};

const PrivacyModuleWebhook = memo(({ user }) => {
	const [activeTab, setActiveTab] = useState(0);
	const tabData = useMemo(
		() => [
			{
				text: "Javascript",
				language: INTEGRATIONS_MODULE_WEBHOOK_CODE_LANGUAGES.JAVASCRIPT,
			},
			{
				text: "Python",
				language: INTEGRATIONS_MODULE_WEBHOOK_CODE_LANGUAGES.PYTHON,
			},
			{
				text: "Node",
				language: INTEGRATIONS_MODULE_WEBHOOK_CODE_LANGUAGES.NODE,
			},
			// {
			// 	text: "PHP",
			// 	language: INTEGRATIONS_MODULE_WEBHOOK_CODE_LANGUAGES.PHP,
			// },
		],
		[]
	);

	const codeDetails = useMemo(
		() =>
			INTEGRATIONS_MODULE_WEBHOOK_CODE({
				user,
			})[tabData[activeTab].language],
		[user, tabData, activeTab]
	);

	const handleTabChange = useCallback(
		({ target: { value } }) => setActiveTab(value),
		[]
	);

	return (
		<SettingsModule>
			<SettingsModuleSection
				title={
					<PaddedContainer centerContent>
						<Asset
							size={20}
							color={FONT_COLOR.DARK}
							marginRight={SPACING.TINY}
						>
							<img src={WEBHOOK_LOGO_IMG} alt="" />
						</Asset>
						Webhook
					</PaddedContainer>
				}
				tightTop
				headerChildren={
					<PaddedContainer
						className="privacy-module-header-children"
						flexContent
					>
						<TabSwitcher
							options={tabData}
							curved
							dynamicWidth
							value={activeTab}
							onChange={handleTabChange}
							resizeOnMount
							small
						/>
					</PaddedContainer>
				}
			>
				<Text
					size={FONT_SIZE.BODY}
					color={FONT_COLOR.MID}
					vPadding={SPACING.REGULAR}
					thin
					className="privacy-module-description"
				>
					Disco provides you webhooks to automatically communicate
					opt-outs from your website in order to honour your
					customers’ privacy. Please follow the instructions below to
					setup webhooks that feed information to Disco
				</Text>
				<Card
					vPadding={SPACING.MEDIUM}
					hPadding={SPACING.MEDIUM}
					noShadow
				>
					<TitleRow
						title="Your script"
						tooltip="This is the script that will be executed when a webhook is triggered"
					/>
					<PaddedContainer vPadding={SPACING.REGULAR} tightBottom>
						<CodeSnippet
							alwaysOpen
							heightParams={{ open: 320, close: 160 }}
							value={codeDetails.code.trim()}
						/>
					</PaddedContainer>
					{INTEGRATION_MODULE_WEBHOOK_INSTRUCTIONS(user).map(
						({ heading, description }) => (
							<PaddedContainer
								vPadding={SPACING.MEDIUM}
								tightBottom
							>
								<TitleRow
									title={heading}
									subtitle={description}
								/>
							</PaddedContainer>
						)
					)}
				</Card>
			</SettingsModuleSection>
		</SettingsModule>
	);
});

const PrivacyModule = memo(
	forwardRef(({ user, setUser }, parentRef) => {
		return (
			<section ref={parentRef}>
				<PrivacyModuleWebhook user={user} setUser={setUser} />
			</section>
		);
	})
);

export default PrivacyModule;
